import { ASSET_STATUS, ASSET_TYPE, ASSET_TYPES, ASSET_TYPE_LABELS, ASSET_STATUS_OPTIONS_BY_TYPE, ASSET_STATUS_LABELS, INSIGHT_KIND, INSIGHT_TYPE, MAX_TAGS_PER_DOC } from '../constants/index.js';

export function readSetting(key, fallback = '') {
  return localStorage.getItem(key) || fallback;
}

export function isUserMessage(message) {
  const source = message.source ?? message.role;
  return source !== 'assistant';
}

export function createMockId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function extractJsonObject(text) {
  let cleaned = text.trim();

  // Remove markdown code blocks if present
  cleaned = cleaned.replace(/^```json\s*/i, '').replace(/^```\s*/i, '').replace(/```$/i, '').trim();

  const start = cleaned.indexOf('{');
  if (start === -1) {
    throw new Error('JSON形式のオブジェクトが見つかりませんでした。');
  }

  let end = -1;
  let inString = false;
  let isEscaped = false;
  const closingStack = [];

  for (let i = start; i < cleaned.length; i++) {
    const char = cleaned[i];

    if (inString) {
      if (isEscaped) {
        isEscaped = false;
        continue;
      }
      if (char === '\\') {
        isEscaped = true;
        continue;
      }
      if (char === '"') {
        inString = false;
      }
      continue;
    }

    if (char === '"') {
      inString = true;
      continue;
    }

    if (char === '{') {
      closingStack.push('}');
      continue;
    }
    if (char === '[') {
      closingStack.push(']');
      continue;
    }
    if (char === '}' || char === ']') {
      if (closingStack.length > 0) {
        const expectedClosing = closingStack[closingStack.length - 1];
        if (char !== expectedClosing) {
          throw new Error('JSON形式が壊れています。');
        }
        closingStack.pop();
        if (closingStack.length === 0) {
          end = i;
          break;
        }
      }
    }
  }

  let jsonStr = end !== -1 ? cleaned.slice(start, end + 1) : cleaned.slice(start);
  if (end === -1 && closingStack.length > 0) {
    jsonStr += closingStack.slice().reverse().join('');
  }

  // Basic sanitization: remove trailing commas in arrays and objects
  // This is a naive regex-based approach but helps with common LLM mistakes
  jsonStr = jsonStr.replace(/,\s*([\]}])/g, '$1');

  try {
    return JSON.parse(jsonStr);
  } catch (e) {
    console.warn('JSON.parse failed even after sanitization:', e);
    throw e;
  }
}

export function normalizeAssetType(type) {
  if (type === ASSET_TYPE.THEME || type === ASSET_TYPE.QUESTION) return ASSET_TYPE.INTEREST;
  // Handle PR #276 names in case any AI/worker generates them
  if (type === 'pattern') return 'framework';
  if (type === 'concept') return 'knowledge';
  if (type === 'action') return 'todo';
  return ASSET_TYPES.includes(type) ? type : 'knowledge';
}

export function inferInsightKind(insight = {}) {
  if (insight.kind) return insight.kind;
  const type = insight.type || '';
  if (
    type === INSIGHT_TYPE.NOTE_UPDATE ||
    type === INSIGHT_TYPE.ASSET_UPDATE ||
    type === INSIGHT_TYPE.TAG_MERGE ||
    type === INSIGHT_TYPE.MISSING_LINK ||
    type.startsWith('duplicate')
  ) {
    return INSIGHT_KIND.PROPOSED_CHANGE;
  }
  return INSIGHT_KIND.HINT;
}

export function isProposedChangeInsight(insight = {}) {
  return inferInsightKind(insight) === INSIGHT_KIND.PROPOSED_CHANGE;
}

export function isHintInsight(insight = {}) {
  return inferInsightKind(insight) === INSIGHT_KIND.HINT;
}

export function getInsightReviewMeta(insight = {}) {
  if ((insight.type || '') === INSIGHT_TYPE.TAG_MERGE) {
    return {
      label: 'タグ整理候補',
      actionSummary: '確認 / 適用 / 却下',
      helperText: 'これは通常のAsset候補や内容更新ではなく、低優先のタグ整理候補です。',
      assetizationReason: 'タグ整理候補のため、Asset化はできません。',
      canAssetize: false,
    };
  }

  if (isProposedChangeInsight(insight)) {
    return {
      label: '更新提案',
      actionSummary: '編集 / 適用 / 却下',
      helperText: 'これはAsset候補ではなく、既存のNote / Asset / Hintへの更新提案です。',
      assetizationReason: 'Asset候補ではないため、Asset化はできません。',
      canAssetize: false,
    };
  }

  return {
    label: 'Asset候補',
    actionSummary: 'Asset化 / 却下 / 保留',
    helperText: '内容を確認し、残したいものだけAssetとして保存できます。',
    assetizationReason: null,
    canAssetize: true,
  };
}

export function buildFallbackInsight(text) {
  const cleaned = text
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/```$/i, '')
    .trim();

  return {
    insights: [
      {
        type: 'implicit_knowledge',
        title: '(パース失敗)',
        summary: cleaned || 'Hint候補は生成されましたが、JSONパースに失敗しました。',
        suggestedTargetType: 'knowledge',
        tags: [],
        confidence: 0.1,
        metadata: {
          parseFailed: true,
          rawOutput: text
        }
      },
    ],
  };
}

export function getAssetTypeLabel(type) {
  return ASSET_TYPE_LABELS[normalizeAssetType(type)];
}

export function getInitialAssetStatus(type) {
  return getAssetStatusOptions(type).includes(ASSET_STATUS.DRAFT)
    ? ASSET_STATUS.DRAFT
    : ASSET_STATUS.ACTIVE;
}

export function getAssetStatusOptions(type) {
  return ASSET_STATUS_OPTIONS_BY_TYPE[normalizeAssetType(type)] || ['draft', 'active', 'archived'];
}

export function getAssetStatusLabel(status) {
  return ASSET_STATUS_LABELS[status] || status;
}

export function sanitizeTags(tags) {
  if (!Array.isArray(tags)) return [];
  const noisePatterns = [
    /^json$/i,
    /^出力$/i,
    /^指示$/i,
    /^配列$/i,
    /^箇条書き$/i,
    /^抽出$/i,
    /^#/,
    /^\d+$/,
    /^[ぁ-ん]$/, // Single hiragana
  ];
  const seen = new Set();
  return tags
    .map(t => t?.toString()?.trim())
    .filter(t => {
      if (!t || t.length < 2 || seen.has(t.toLowerCase())) return false;
      if (noisePatterns.some(p => p.test(t))) return false;
      seen.add(t.toLowerCase());
      return true;
    })
    .slice(0, MAX_TAGS_PER_DOC);
}

export function mapErrorToUserMessage(error) {
  const msg = typeof error === 'string' ? error : error?.message || '';

  if (msg.includes('byok_only') || msg.includes('API key') || msg.includes('401') || msg.includes('invalid_api_key') || msg.includes('ログインしていません')) {
    return '[API_KEY_MISSING] APIキーが設定されていないか、正しくありません。Settingsから設定を確認してください。';
  }
  if (msg.includes('Rate limit') || msg.includes('429') || msg.includes('Quota exceeded') || msg.includes('RESOURCE_EXHAUSTED')) {
    return 'APIの利用上限に達しました。しばらく待ってから再試行してください';
  }
  if (msg.includes('timeout') || msg.includes('AbortError')) {
    return '接続がタイムアウトしました。再送信してください';
  }
  if (msg.includes('JSON') || msg.includes('parse')) {
    return 'AIの返答の解析に失敗しました。再送信してください';
  }
  if (msg.includes('Probレポート') || msg.includes('Shared Asset')) {
    return msg;
  }

  return '返答の取得に失敗しました';
}

/**
 * Checks if the user has permission to view shared assets.
 */
export function canViewSharedAssets(userData) {
  if (!userData) return false;
  const productRoles = userData.productRoles || {};
  return (
    userData.role === 'owner' ||
    userData.featureFlags?.canViewSharedAssets === true ||
    ['light', 'standard', 'deep', 'byok'].includes(userData.plan) ||
    ['member', 'byok', 'admin'].includes(productRoles.sink) ||
    ['viewer', 'admin'].includes(productRoles.prob)
  );
}

/**
 * Checks if the user has permission to discuss shared assets.
 */
export function canDiscussSharedAssets(userData) {
  if (!userData) return false;
  const productRoles = userData.productRoles || {};
  return (
    userData.role === 'owner' ||
    userData.featureFlags?.canDiscussSharedAssets === true ||
    ['member', 'byok', 'admin'].includes(productRoles.sink) ||
    ['viewer', 'admin'].includes(productRoles.prob)
  );
}

/**
 * Checks if the user can view current Prob reports.
 */
export function canViewProbReports(userData) {
  if (!userData) return false;
  const productRoles = userData.productRoles || {};
  return (
    userData.role === 'owner' ||
    ['viewer', 'admin'].includes(productRoles.prob) ||
    userData.featureFlags?.canViewProb === true
  );
}

/**
 * Checks if the user can use Sink's existing authenticated surfaces.
 */
export function canUseSink(userData) {
  if (!userData) return false;
  const productRoles = userData.productRoles || {};
  const hasSinkRole = ['member', 'byok', 'admin'].includes(productRoles.sink);
  return (
    userData.role === 'owner' ||
    hasSinkRole ||
    ['credit_beta', 'standard_user', 'byok_user'].includes(userData.role)
  );
}

export function canDiscussSharedAssetRef(userData, ref) {
  if (ref?.sourceApp === 'prob') {
    return canUseSink(userData) && canViewProbReports(userData);
  }
  return canViewSharedAssets(userData) && canDiscussSharedAssets(userData);
}

/**
 * Resolves knowledge IDs in text (e.g. [Asset ID: ...]) with descriptive, truncated titles.
 */
export function resolveKnowledgeLinks(text, collections = {}) {
  if (!text) return text;
  const { concepts = [], notes = [], insights = [] } = collections;

  return text.replace(/\[(Asset|Note|Hint) ID: ([^,\]\s]+)(?:, Title: ([^\]]+))?\]/g, (match, type, id, capturedTitle) => {
    let collection;
    if (type === 'Asset') collection = concepts;
    else if (type === 'Note') collection = notes;
    else if (type === 'Hint') collection = insights;

    const item = collection?.find(i => i.id === id);
    const title = item?.title || capturedTitle;

    if (title) {
      const displayTitle = title.length > 25 ? title.slice(0, 25) + '...' : title;
      return `**${type}: ${displayTitle}**`;
    }

    return match;
  });
}
